import { Eye, Loader2, RotateCw } from 'lucide-react';
import { useState } from 'react';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { Button } from '~/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { cn } from '~/utils/cn';
import { formatDateTime, formatDurationBetween } from '~/utils/date';

import { useBatchRuns } from '../hooks/use-batch-retry';
import type { BatchRetryScope, BatchRun } from '../types/batch-retry.api.types';

import { BatchRetryDialog } from './batch-retry/batch-retry-dialog';
import { RunOutcomeBar } from './batch-retry/batch-retry-shared';
import { formatCount, runTotal } from './batch-retry/batch-retry-utils';

const SCOPE_LABEL: Record<BatchRetryScope, string> = {
  REMAINING: 'All remaining',
  FAILED: 'Failed only',
  NOT_ATTEMPTED: 'Not reached only',
};

const RUN_STATUS: Record<string, { label: string; className: string }> = {
  RUNNING: { label: 'Running', className: 'bg-info-50 text-info' },
  COMPLETED: { label: 'Completed', className: 'bg-success-50 text-success' },
  FAILED: { label: 'Failed', className: 'bg-error-50 text-error' },
  TERMINATED: { label: 'Terminated', className: 'bg-muted text-muted-foreground' },
};

/** Column headers carry the outcome bar's colours, so the sub-table needs no legend. */
const OUTCOME_COLUMNS = [
  { key: 'succeeded', label: 'Enriched', dot: 'bg-emerald-500' },
  { key: 'failed', label: 'Failed', dot: 'bg-error' },
  { key: 'notAttempted', label: 'Not reached', dot: 'bg-warning' },
] as const;

/** Pill for a retry run's own status (never the batch's). */
export function RunStatusPill({
  status,
  paused = false,
}: {
  status: string | null;
  paused?: boolean;
}) {
  const cfg =
    paused && status === 'RUNNING'
      ? { label: 'Paused', className: 'bg-muted text-muted-foreground' }
      : (RUN_STATUS[status ?? ''] ?? {
          label: status ?? '—',
          className: 'bg-muted text-muted-foreground',
        });
  return (
    <span
      className={cn('inline-flex rounded-md px-2 py-0.5 text-xs font-medium', cfg.className)}
      data-testid="batch-child-run-status"
    >
      {cfg.label}
    </span>
  );
}

function ChildRunRow({
  run,
  parentName,
  paused,
  onOpen,
  onRetry,
}: {
  run: BatchRun;
  parentName: string | undefined;
  paused: boolean;
  onOpen: (runId: string) => void;
  onRetry: (runId: string) => void;
}) {
  const picked = runTotal(run);
  const running = run.status === 'RUNNING';
  const done = run.done ?? 0;
  const pct = picked > 0 ? Math.min(100, (done / picked) * 100) : 0;
  const leftover = (run.failed ?? 0) + (run.notAttempted ?? 0);
  const duration =
    run.startedAt && run.completedAt ? formatDurationBetween(run.startedAt, run.completedAt) : '';
  return (
    <TableRow
      data-testid={`batch-child-run-${run.runId}`}
      className="cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        onOpen(run.runId);
      }}
    >
      <TableCell className="font-semibold whitespace-nowrap">Retry #{run.attempt}</TableCell>
      <TableCell>
        <RunStatusPill status={run.status} paused={paused} />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {run.scope ? SCOPE_LABEL[run.scope] : 'What it was given'}
        {parentName && (
          <span className="text-muted-foreground block text-xs">from {parentName}</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {run.startedAt ? formatDateTime(run.startedAt) : '—'}
        {(run.requestedBy ?? duration) && (
          <span className="text-muted-foreground flex items-center gap-1 text-xs">
            {run.requestedBy && <ActorStringLabel value={run.requestedBy} showAvatar={false} />}
            {run.requestedBy && duration && <span aria-hidden="true">·</span>}
            {duration}
          </span>
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">{formatCount(picked)}</TableCell>
      {OUTCOME_COLUMNS.map((c) => (
        <TableCell
          key={c.key}
          data-testid={`batch-child-run-${c.key}-${run.runId}`}
          className="text-right tabular-nums"
        >
          {running ? '—' : formatCount(run[c.key])}
        </TableCell>
      ))}
      <TableCell data-testid={`batch-child-run-result-${run.runId}`}>
        {running ? (
          <div className="w-28 space-y-1">
            <div
              className="bg-muted h-2 overflow-hidden rounded-full"
              role="img"
              aria-label={`${done.toLocaleString()} of ${picked.toLocaleString()} done`}
            >
              <div className="bg-info h-full rounded-full" style={{ width: `${String(pct)}%` }} />
            </div>
            <span className="text-muted-foreground text-xs tabular-nums">
              {formatCount(done)} of {formatCount(picked)} done
            </span>
          </div>
        ) : (
          <RunOutcomeBar
            run={run}
            total={picked}
            label={`${formatCount(run.succeeded)} enriched, ${formatCount(run.failed)} failed, ${formatCount(run.notAttempted)} not reached of ${formatCount(picked)}`}
            className="h-2 w-28"
          />
        )}
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          <Button
            data-testid={`batch-child-run-open-${run.runId}`}
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(run.runId);
            }}
          >
            <Eye aria-hidden="true" /> {running ? 'View progress' : 'View products'}
          </Button>
          {run.availableActions.includes('RETRY_RUN_FAILURES') && (
            <Button
              data-testid={`batch-child-run-retry-${run.runId}`}
              size="sm"
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onRetry(run.runId);
              }}
            >
              <RotateCw aria-hidden="true" /> Retry {formatCount(leftover)}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

interface BatchChildRunsProps {
  jobId: string;
  /** Navigates to the batch detail's Run Details tab with this run selected. */
  onOpenRun: (runId: string) => void;
}

/**
 * Retries of a grid parent batch, as a sub-table: one row per retry with its scope, who started
 * it, its outcome counts and its actions. Fetched lazily: mounts only when the row expands.
 */
export function BatchChildRuns({ jobId, onOpenRun }: BatchChildRunsProps) {
  const { data, isLoading, isError } = useBatchRuns(jobId);
  const [retryFrom, setRetryFrom] = useState<string | null>(null);
  const children = (data?.runs ?? []).filter((r) => r.kind === 'RETRY');
  const paused = data?.batchStatus === 'PAUSED';

  if (isLoading) {
    return (
      <div
        data-testid="batch-child-runs-loading"
        className="text-muted-foreground flex items-center gap-2 text-sm"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading child runs…
      </div>
    );
  }
  if (isError) {
    return (
      <p data-testid="batch-child-runs-error" className="text-muted-foreground text-sm">
        Could not load child runs.
      </p>
    );
  }
  if (children.length === 0) {
    return (
      <p data-testid="batch-child-runs-empty" className="text-muted-foreground text-sm">
        No retries yet. Open the batch to see its unenriched products and retry them.
      </p>
    );
  }
  const nameById = new Map(
    (data?.runs ?? []).map((r) => [
      r.runId,
      r.kind === 'ROOT' ? 'original run' : `retry #${String(r.attempt)}`,
    ]),
  );
  const left = data
    ? data.counts.failedRetryable + data.counts.failedPermanent + (data.counts.notAttempted ?? 0)
    : 0;
  return (
    // Sized to its content and pinned left: the grid is wider than the screen, so a full-width
    // table would push the actions off-screen.
    <div data-testid="batch-child-runs" className="sticky left-0 w-max max-w-full space-y-2">
      <p className="text-muted-foreground text-xs">
        <span className="text-foreground text-sm font-semibold">Retries</span>
        {data && ` · ${String(data.attemptsUsed)} of ${String(data.maxAttempts)} used`}
        {left > 0 && ` · ${formatCount(left)} products still not enriched`}
        {' · click a run to open it in Run Details'}
      </p>
      <Table containerClassName="rounded-lg border bg-white">
        <TableHeader className="bg-muted/40">
          <TableRow>
            <TableHead>Run</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Covered</TableHead>
            <TableHead>Started</TableHead>
            <TableHead className="text-right">Picked</TableHead>
            {OUTCOME_COLUMNS.map((c) => (
              <TableHead key={c.key} className="text-right whitespace-nowrap">
                <span className={cn('mr-1.5 inline-block size-2 rounded-full', c.dot)} />
                {c.label}
              </TableHead>
            ))}
            <TableHead>Result</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {children.map((run) => (
            <ChildRunRow
              key={run.runId}
              run={run}
              parentName={run.parentRunId ? nameById.get(run.parentRunId) : undefined}
              paused={paused}
              onOpen={onOpenRun}
              onRetry={setRetryFrom}
            />
          ))}
        </TableBody>
      </Table>
      <BatchRetryDialog
        jobId={jobId}
        open={retryFrom !== null}
        onOpenChange={(open) => {
          if (!open) setRetryFrom(null);
        }}
        {...(retryFrom ? { fromRunId: retryFrom } : {})}
      />
    </div>
  );
}
